import axios from 'axios'
import type { VariantPrice } from '../types/ProductListing'
import * as ApiSettingService from "src/db/serviceApiSetting"
import { env } from "src/config/env"

const BASE_URL = env.SERVER_URL

export interface PriceUpdateApiRequest {
  account_id: string
  updates: PriceUpdateItem[]
}

export interface PriceUpdateItem {
  sku?: string
  listing_id?: string
  variant_sku?: string
  listing_variant_id?: string
  new_price: number
}

export interface PriceUpdateApiResponse {
  process_id: string
  status: 'queued'
  total_products: number
}

export interface MarketplacePriceConstraints {
  marketplace: string
  minimumPrice: number
  currency: string
}

export class PriceUpdateService {
  private static instance: PriceUpdateService

  static getInstance(): PriceUpdateService {
    if (!this.instance) {
      this.instance = new PriceUpdateService()
    }
    return this.instance
  }

  private getMarketplacePriceConstraints(): Record<string, MarketplacePriceConstraints> {
    return {
      shopee: {
        marketplace: 'shopee',
        minimumPrice: 1.00,
        currency: 'IDR'
      },
      lazada: {
        marketplace: 'lazada', 
        minimumPrice: 0.50,
        currency: 'IDR'
      },
      tokopedia: {
        marketplace: 'tokopedia',
        minimumPrice: 100.00,
        currency: 'IDR'
      },
      tiktok: {
        marketplace: 'tiktok',
        minimumPrice: 1.00,
        currency: 'IDR'
      }
    }
  }

  validatePriceForMarketplace(marketplace: string, price: number): { valid: boolean; error?: string } {
    const constraints = this.getMarketplacePriceConstraints()
    const constraint = constraints[marketplace.toLowerCase()]
    
    if (!constraint) {
      return {
        valid: false,
        error: `Unknown marketplace: ${marketplace}`
      }
    }

    if (price < 0.01) {
      return {
        valid: false,
        error: 'Price must be at least 0.01'
      }
    }

    if (price > 99999999) {
      return {
        valid: false,
        error: 'Price cannot exceed 99,999,999'
      }
    }

    if (Math.round(price * 100) !== price * 100) {
      return {
        valid: false,
        error: 'Price can have maximum 2 decimal places'
      }
    }

    if (price < constraint.minimumPrice) {
      return {
        valid: false,
        error: `Price below marketplace minimum: ${constraint.marketplace} requires minimum ${constraint.minimumPrice}`
      }
    }

    return { valid: true }
  }

  validateBatchPriceUpdates(
    variants: VariantPrice[],
    marketplace: string
  ): { valid: boolean; errors: Record<string, string> } {
    const errors: Record<string, string> = {}
    
    variants.forEach(variant => {
      if (variant.newPrice > 0) {
        const validation = this.validatePriceForMarketplace(marketplace, variant.newPrice)
        if (!validation.valid) {
          errors[variant.variantId] = validation.error || 'Invalid price'
        }
      }
    })

    return {
      valid: Object.keys(errors).length === 0,
      errors
    }
  }

  private transformVariantsToPriceUpdates(
    variants: VariantPrice[],
    productSku?: string,
    productListingId?: string
  ): PriceUpdateItem[] {
    return variants
      .filter(variant => variant.newPrice > 0)
      .map(variant => {
        const updateItem: PriceUpdateItem = {
          new_price: variant.newPrice
        };

        // 1. If product has sku, use sku; otherwise use listing_id
        if (productSku) {
          updateItem.sku = productSku;
        } else if (productListingId) {
          updateItem.listing_id = productListingId;
        }

        // 2. Only add variant fields if this is a multi-variant product (not "main")
        if (variant.variantId !== "main") {
          if (variant.sku) {
            updateItem.variant_sku = variant.sku;
          } else if (variant.variantId) {
            updateItem.listing_variant_id = variant.variantId;
          }
        }

        return updateItem;
      })
  }

  async updateProductPrices(
    accountId: string,
    variants: VariantPrice[],
    marketplace: string,
    productSku?: string,
    productListingId?: string
  ): Promise<PriceUpdateApiResponse> {
    try {
      const accountAccess = await ApiSettingService.getAccess()
      if (!accountAccess) {
        throw new Error("Authentication failed - unable to get access token")
      }

      const validation = this.validateBatchPriceUpdates(variants, marketplace)
      if (!validation.valid) {
        const firstError = Object.values(validation.errors)[0]
        throw new Error(firstError)
      }

      const updates = this.transformVariantsToPriceUpdates(variants, productSku, productListingId)
      if (updates.length === 0) {
        throw new Error("No valid price updates to process")
      }

      if (updates.length > 1000) {
        throw new Error("Maximum 1,000 updates per batch allowed")
      }

      const requestBody: PriceUpdateApiRequest = {
        account_id: accountId,
        updates
      }

      console.log("[PriceUpdateService] === Transformed Updates ===");
      console.log("[PriceUpdateService] Product SKU:", productSku || 'N/A');
      console.log("[PriceUpdateService] Product Listing ID:", productListingId || 'N/A');
      console.log("[PriceUpdateService] Updates:", JSON.stringify(updates, null, 2));

      const response = await axios.post<PriceUpdateApiResponse>(
        `${BASE_URL}/api/v2/product_listing/price`,
        requestBody,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accountAccess.accessToken}`,
          },
        }
      )

      return response.data
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          const message = error.response.data?.message || 'Invalid request data'
          throw new Error(`Validation failed: ${message}`)
        }
        if (error.response?.status === 401) {
          throw new Error('Authentication failed - please check your credentials')
        }
        if (error.response?.status === 404) {
          throw new Error('Account not found')
        }
        if (error.response?.status === 500) {
          throw new Error('Server error - price update process creation failed')
        }
      }
      
      console.error('Error updating product prices:', error)
      throw error instanceof Error ? error : new Error('Failed to update product prices')
    }
  }

}

export default PriceUpdateService